/**
 * 线索财务配置模态框组件
 * 用于配置线索的付费模式和相关参数
 */
import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Radio,
  message,
  Space,
  Divider,
  Alert,
} from 'antd';
import { PaymentMode } from '../../types/financeConfig';
import type { LeadFinanceConfig, LeadFinanceConfigCreate } from '../../types/financeConfig';
import { createOrUpdateLeadFinanceConfig } from '../../services/financeConfig';

const { TextArea } = Input;

interface LeadFinanceConfigModalProps {
  visible: boolean;
  leadId: number;
  config?: LeadFinanceConfig | null;
  onCancel: () => void;
  onSuccess: () => void;
}

const LeadFinanceConfigModal: React.FC<LeadFinanceConfigModalProps> = ({
  visible,
  leadId,
  config,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(PaymentMode.FIXED);

  useEffect(() => {
    if (visible && config) {
      form.setFieldsValue({
        payment_mode: config.payment_mode,
        contract_amount: config.contract_amount,
        risk_base_fee: config.risk_base_fee,
        risk_success_rate: config.risk_success_rate ? config.risk_success_rate * 100 : undefined,
        risk_target_amount: config.risk_target_amount,
        risk_payment_condition: config.risk_payment_condition,
        risk_min_fee: config.risk_min_fee,
        risk_max_fee: config.risk_max_fee,
        hourly_rate: config.hourly_rate,
        estimated_hours: config.estimated_hours,
        notes: config.notes,
      });
      setPaymentMode(config.payment_mode);
    } else if (visible) {
      form.resetFields();
      setPaymentMode(PaymentMode.FIXED);
    }
  }, [visible, config, form]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // 转换百分比为小数
      if (values.risk_success_rate) {
        values.risk_success_rate = values.risk_success_rate / 100;
      }

      const data: LeadFinanceConfigCreate = {
        lead_id: leadId,
        payment_mode: values.payment_mode,
        contract_amount: values.contract_amount,
        notes: values.notes,
        risk_base_fee: values.risk_base_fee,
        risk_success_rate: values.risk_success_rate,
        risk_target_amount: values.risk_target_amount,
        risk_payment_condition: values.risk_payment_condition,
        risk_min_fee: values.risk_min_fee,
        risk_max_fee: values.risk_max_fee,
        hourly_rate: values.hourly_rate,
        estimated_hours: values.estimated_hours,
      };

      await createOrUpdateLeadFinanceConfig(leadId, data);
      message.success(config ? '财务配置更新成功' : '财务配置创建成功');
      onSuccess();
    } catch (error: any) {
      console.error('Failed to save finance config:', error);
      if (error.errorFields) {
        return;
      }
      message.error(error.message || '保存失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  const renderModeSpecificFields = () => {
    switch (paymentMode) {
      case PaymentMode.MILESTONE:
        return (
          <Alert
            message="节点付费模式"
            description="保存配置后，请在下方的付费节点管理中添加具体的付费节点。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        );

      case PaymentMode.RISK_AGENCY:
        return (
          <>
            <Divider>风险代理配置</Divider>
            <Form.Item
              label="基础费用"
              name="risk_base_fee"
              rules={[{ required: true, message: '请输入基础费用' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                placeholder="请输入基础费用"
                addonBefore="¥"
              />
            </Form.Item>

            <Form.Item
              label="成功比例"
              name="risk_success_rate"
              rules={[{ required: true, message: '请输入成功比例' }]}
              tooltip="案件胜诉或达成目标后，按标的额收取的比例"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                max={100}
                precision={2}
                placeholder="请输入成功比例"
                addonAfter="%"
              />
            </Form.Item>

            <Form.Item
              label="预期标的额"
              name="risk_target_amount"
              tooltip="预期的案件标的金额"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                placeholder="请输入预期标的额"
                addonBefore="¥"
              />
            </Form.Item>

            <Form.Item
              label="付费条件"
              name="risk_payment_condition"
              tooltip="触发风险代理费用的具体条件"
            >
              <TextArea
                rows={3}
                placeholder="例如：胜诉后支付、执行到位后支付等"
              />
            </Form.Item>

            <Space style={{ width: '100%' }} size="large">
              <Form.Item
                label="最低收费"
                name="risk_min_fee"
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  placeholder="最低收费"
                  addonBefore="¥"
                />
              </Form.Item>

              <Form.Item
                label="最高收费"
                name="risk_max_fee"
                style={{ marginBottom: 0 }}
              >
                <InputNumber
                  min={0}
                  precision={2}
                  placeholder="最高收费"
                  addonBefore="¥"
                />
              </Form.Item>
            </Space>
          </>
        );

      case PaymentMode.HOURLY:
        return (
          <>
            <Divider>按小时计费配置</Divider>
            <Form.Item
              label="小时费率"
              name="hourly_rate"
              rules={[{ required: true, message: '请输入小时费率' }]}
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={2}
                placeholder="请输入小时费率"
                addonBefore="¥"
                addonAfter="/小时"
              />
            </Form.Item>

            <Form.Item
              label="预估工时"
              name="estimated_hours"
              tooltip="预计投入的总工时"
            >
              <InputNumber
                style={{ width: '100%' }}
                min={0}
                precision={0}
                placeholder="请输入预估工时"
                addonAfter="小时"
              />
            </Form.Item>

            {form.getFieldValue('hourly_rate') && form.getFieldValue('estimated_hours') && (
              <Alert
                message={`预估总费用：¥${(
                  form.getFieldValue('hourly_rate') * form.getFieldValue('estimated_hours')
                ).toFixed(2)}`}
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Modal
      title={config ? '编辑财务配置' : '创建财务配置'}
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      width={600}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          payment_mode: PaymentMode.FIXED,
          contract_amount: 0,
        }}
      >
        <Form.Item
          label="付费模式"
          name="payment_mode"
          rules={[{ required: true, message: '请选择付费模式' }]}
        >
          <Radio.Group
            onChange={(e) => setPaymentMode(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value={PaymentMode.FIXED}>固定收费</Radio.Button>
            <Radio.Button value={PaymentMode.MILESTONE}>节点付费</Radio.Button>
            <Radio.Button value={PaymentMode.RISK_AGENCY}>风险代理</Radio.Button>
            <Radio.Button value={PaymentMode.HOURLY}>按小时计费</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="合同金额"
          name="contract_amount"
          rules={[{ required: true, message: '请输入合同金额' }]}
          tooltip={
            paymentMode === PaymentMode.MILESTONE
              ? '节点付费模式下，合同金额应等于所有节点金额之和'
              : undefined
          }
        >
          <InputNumber
            style={{ width: '100%' }}
            min={0}
            precision={2}
            placeholder="请输入合同金额"
            addonBefore="¥"
          />
        </Form.Item>

        {renderModeSpecificFields()}

        <Form.Item label="备注说明" name="notes">
          <TextArea
            rows={3}
            placeholder="请输入备注说明"
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default LeadFinanceConfigModal;
